import React, { type ReactNode } from 'react';
import { Banner, Divider, GoogleButton, Spinner, useTheme, type GoogleButtonRenderOptions } from '@talbenmoshe/trip-os-design-system';
import { GoogleSignInAvailability, SignInActivity, useGoogleSignIn, useSignIn } from '@talbenmoshe/trip-os-react-model';
import { useGoogleButtonRenderer } from '../google/GoogleButtonRendererContext.js';
import { useMessages } from '../i18n/messagesContext.js';

/**
 * "or" and "Continue with Google" under "Send me a code" (Sign in · C1, C3). Nothing shows while Google is being
 * prepared; when it cannot be prepared the divider stays and an information banner takes the button's place (Tal's
 * clarification, 2026-10-20 — undrawn, composed from drawn components, like the "Signing you in…" spinner).
 */
export function GoogleSignInSection() {
  const messages = useMessages();
  const availability = useGoogleSignIn();
  const signIn = useSignIn();
  const renderer = useGoogleButtonRenderer();
  const theme = useTheme();
  let section: ReactNode = null;

  function renderGoogleButton(container: HTMLElement, options: GoogleButtonRenderOptions): void {
    renderer.renderButton(container, options);
  }

  if (availability === GoogleSignInAvailability.AVAILABLE) {
    section = (
      <>
        <Divider label={messages.SIGN_IN_OR()} />
        <GoogleButton theme={theme} fullWidth disabled={signIn.isBusy} renderButton={renderGoogleButton} />
        {signIn.activity === SignInActivity.SIGNING_IN_WITH_GOOGLE && <Spinner size={16} label={messages.SIGN_IN_SIGNING_IN_WITH_GOOGLE()} />}
      </>
    );
  } else if (availability === GoogleSignInAvailability.UNAVAILABLE) {
    section = (
      <>
        <Divider label={messages.SIGN_IN_OR()} />
        <Banner tone="info" title={messages.SIGN_IN_GOOGLE_UNAVAILABLE_TITLE()} body={messages.SIGN_IN_GOOGLE_UNAVAILABLE_BODY()} />
      </>
    );
  }

  return section;
}
